import type {
  CarriersLocationCoverages,
  CarriersInsuranceHistory,
} from 'api/quote-marketplace';
import { formatCurrencyNumber } from '../constants';

export interface CoverageRowConfig {
  label: string;
  getRequestedValue: (
    coverages: CarriersLocationCoverages,
    location: {
      building_coverage: number | null;
      outbuildings: number | null;
      contents: number | null;
    },
  ) => string;
  getRatedValue?: (
    ratedCoverages: Record<string, unknown>,
  ) => string | undefined;
  getRawRequestedValue?: (
    coverages: CarriersLocationCoverages,
    location: {
      building_coverage: number | null;
      outbuildings: number | null;
      contents: number | null;
    },
  ) => unknown;
  getRawRatedValue?: (ratedCoverages: Record<string, unknown>) => unknown;
  isRequested?: (
    coverages: CarriersLocationCoverages,
    location: {
      building_coverage: number | null;
      outbuildings: number | null;
      contents: number | null;
    },
  ) => boolean;
  codeField?: string;
}

export interface CoverageSectionConfig {
  key: string;
  label: string;
  rows: CoverageRowConfig[];
}

export interface DiscountLocation {
  burglar_alarm: string;
  fire_alarm: string;
  number_of_mortgages: number | null;
  claims_count: number;
}

export interface DiscountRowConfig {
  label: string;
  getRequestedValue: (
    location: DiscountLocation,
    insuranceHistory: CarriersInsuranceHistory,
  ) => string;
  getRatedValue: (
    ratedCoverages: Record<string, unknown>,
  ) => string | undefined;
  getRawRequestedValue: (
    location: DiscountLocation,
    insuranceHistory: CarriersInsuranceHistory,
  ) => unknown;
  getRawRatedValue: (ratedCoverages: Record<string, unknown>) => boolean;
  isRequested: (
    location: DiscountLocation,
    insuranceHistory: CarriersInsuranceHistory,
  ) => boolean;
}

export interface DiscountSectionConfig {
  key: string;
  label: string;
  rows: DiscountRowConfig[];
}

const formatCoverageValue = (
  limit: number | null,
  deductible?: number | null,
  isEnabled?: boolean,
): string => {
  if (limit === null && deductible === null) {
    return isEnabled ? 'Base' : '-';
  }
  if (limit === null) {
    return deductible !== null
      ? `Base / ${formatCurrencyNumber(deductible)} ded.`
      : '-';
  }
  let result = formatCurrencyNumber(limit);
  if (deductible !== null && deductible !== undefined) {
    result += ` / ${formatCurrencyNumber(deductible)} ded.`;
  }
  return result;
};

export const CORE_COVERAGE_ROWS: CoverageRowConfig[] = [
  {
    label: 'Liability',
    getRequestedValue: (coverages) =>
      formatCurrencyNumber(coverages.liability_limit_cad),
    getRatedValue: (rated) => {
      const legalLiability = rated?.legal_liability as
        | Record<string, unknown>
        | undefined;
      return legalLiability?.limit_cad
        ? formatCurrencyNumber(legalLiability.limit_cad as number)
        : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.liability_limit_cad,
    getRawRatedValue: (rated) =>
      (rated?.legal_liability as Record<string, unknown> | undefined)
        ?.limit_cad,
    isRequested: () => true,
  },
  {
    label: 'Dwelling',
    getRequestedValue: (_, location) =>
      formatCurrencyNumber(location.building_coverage),
    getRatedValue: (rated) => {
      const dwelling = rated?.dwelling_building as
        | Record<string, unknown>
        | undefined;
      return dwelling?.limit_cad
        ? formatCurrencyNumber(dwelling.limit_cad as number)
        : undefined;
    },
    getRawRequestedValue: (_, location) => location.building_coverage,
    getRawRatedValue: (rated) =>
      (rated?.dwelling_building as Record<string, unknown> | undefined)
        ?.limit_cad,
    isRequested: (_, location) => location.building_coverage != null,
  },
  {
    label: 'Outbuildings',
    getRequestedValue: (_, location) =>
      formatCurrencyNumber(location.outbuildings),
    getRatedValue: (rated) => {
      const structures = rated?.other_structures as
        | Record<string, unknown>
        | undefined;
      return structures?.limit_cad
        ? formatCurrencyNumber(structures.limit_cad as number)
        : undefined;
    },
    getRawRequestedValue: (_, location) => location.outbuildings,
    getRawRatedValue: (rated) =>
      (rated?.other_structures as Record<string, unknown> | undefined)
        ?.limit_cad,
    isRequested: (_, location) => location.outbuildings != null,
  },
  {
    label: 'Contents',
    getRequestedValue: (_, location) => formatCurrencyNumber(location.contents),
    getRatedValue: (rated) => {
      const property = rated?.personal_property as
        | Record<string, unknown>
        | undefined;
      return property?.limit_cad
        ? formatCurrencyNumber(property.limit_cad as number)
        : undefined;
    },
    getRawRequestedValue: (_, location) => location.contents,
    getRawRatedValue: (rated) =>
      (rated?.personal_property as Record<string, unknown> | undefined)
        ?.limit_cad,
    isRequested: (_, location) => location.contents != null,
  },
  {
    label: 'Additional Living Expense',
    getRequestedValue: () => '',
    getRatedValue: (rated) => {
      const ale = rated?.additional_living_expense as
        | Record<string, unknown>
        | undefined;
      return ale?.limit_cad
        ? formatCurrencyNumber(ale.limit_cad as number)
        : undefined;
    },
  },
  {
    label: 'Deductible',
    getRequestedValue: (coverages) =>
      formatCurrencyNumber(coverages.deductible_cad),
    getRatedValue: (rated) => {
      const dwelling = rated?.dwelling_building as
        | Record<string, unknown>
        | undefined;
      return dwelling?.deductible_cad != null
        ? formatCurrencyNumber(dwelling.deductible_cad as number)
        : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.deductible_cad,
    getRawRatedValue: (rated) =>
      (rated?.dwelling_building as Record<string, unknown> | undefined)
        ?.deductible_cad,
    isRequested: () => true,
  },
];

export const INDICATOR_ROWS: CoverageRowConfig[] = [
  {
    label: 'Single Limit',
    getRequestedValue: (coverages) =>
      coverages.single_limit_enabled ? 'Yes' : 'No',
    getRatedValue: (rated) => {
      const singleLimit = rated?.single_limit as
        | Record<string, unknown>
        | undefined;
      if (!singleLimit) return undefined;
      const limit = singleLimit.limit_cad as number | undefined;
      return limit != null && limit > 0
        ? formatCurrencyNumber(limit)
        : limit === 0
          ? 'No'
          : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.single_limit_enabled,
    getRawRatedValue: (rated) => {
      const singleLimit = rated?.single_limit as
        | Record<string, unknown>
        | undefined;
      if (!singleLimit) return null;
      const limit = singleLimit.limit_cad as number | undefined;
      return limit != null && limit > 0;
    },
    isRequested: (coverages) => coverages.single_limit_enabled,
  },
  {
    label: 'GBRC',
    getRequestedValue: (coverages) => (coverages.grbc_enabled ? 'Yes' : 'No'),
    getRatedValue: (rated) => {
      const gbrc = rated?.guaranteed_replacement_cost_building as
        | Record<string, unknown>
        | undefined;
      return gbrc ? 'Yes' : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.grbc_enabled,
    getRawRatedValue: (rated) =>
      !!(rated?.guaranteed_replacement_cost_building as
        | Record<string, unknown>
        | undefined),
    isRequested: (coverages) => coverages.grbc_enabled,
  },
];

export const EXTENDED_COVERAGE_ROWS: CoverageRowConfig[] = [
  {
    label: 'Sewer Backup',
    getRequestedValue: (coverages) =>
      coverages.sewer_backup_enabled
        ? formatCoverageValue(
            coverages.sewer_backup_limit_cad,
            coverages.sewer_backup_deductible_cad,
            true,
          )
        : 'No',
    getRatedValue: (rated) => {
      const sewerBackup = rated?.sewer_backup as
        | Record<string, unknown>
        | undefined;
      if (!sewerBackup) return undefined;
      const limit = sewerBackup.limit_cad as number | undefined;
      const deductible = sewerBackup.deductible_cad as number | undefined;
      return limit != null ? formatCoverageValue(limit, deductible) : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.sewer_backup_limit_cad,
    getRawRatedValue: (rated) =>
      (rated?.sewer_backup as Record<string, unknown> | undefined)?.limit_cad,
    isRequested: (coverages) => coverages.sewer_backup_enabled,
  },
  {
    label: 'Ground Water',
    getRequestedValue: (coverages) =>
      coverages.ground_water_enabled
        ? formatCoverageValue(
            coverages.ground_water_limit_cad,
            coverages.ground_water_deductible_cad,
            true,
          )
        : 'No',
    getRatedValue: (rated) => {
      const groundWater = rated?.ground_water as
        | Record<string, unknown>
        | undefined;
      if (!groundWater) return undefined;
      const limit = groundWater.limit_cad as number | undefined;
      const deductible = groundWater.deductible_cad as number | undefined;
      return limit != null ? formatCoverageValue(limit, deductible) : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.ground_water_limit_cad,
    getRawRatedValue: (rated) =>
      (rated?.ground_water as Record<string, unknown> | undefined)?.limit_cad,
    isRequested: (coverages) => coverages.ground_water_enabled,
  },
  {
    label: 'Above Ground Water',
    getRequestedValue: (coverages) =>
      coverages.above_ground_water_enabled
        ? formatCoverageValue(
            coverages.above_ground_water_limit_cad,
            coverages.above_ground_water_deductible_cad,
            true,
          )
        : 'No',
    getRatedValue: (rated) => {
      const aboveGroundWater = rated?.above_ground_water as
        | Record<string, unknown>
        | undefined;
      if (!aboveGroundWater) return undefined;
      const limit = aboveGroundWater.limit_cad as number | undefined;
      const deductible = aboveGroundWater.deductible_cad as number | undefined;
      return limit != null ? formatCoverageValue(limit, deductible) : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.above_ground_water_limit_cad,
    getRawRatedValue: (rated) =>
      (rated?.above_ground_water as Record<string, unknown> | undefined)
        ?.limit_cad,
    isRequested: (coverages) => coverages.above_ground_water_enabled,
  },
  {
    label: 'Overland Water',
    getRequestedValue: (coverages) =>
      coverages.overland_water_enabled
        ? formatCoverageValue(
            coverages.overland_water_limit_cad,
            coverages.overland_water_deductible_cad,
            true,
          )
        : 'No',
    getRatedValue: (rated) => {
      const overlandWater = rated?.overland_water as
        | Record<string, unknown>
        | undefined;
      if (!overlandWater) return undefined;
      const limit = overlandWater.limit_cad as number | undefined;
      const deductible = overlandWater.deductible_cad as number | undefined;
      return limit != null ? formatCoverageValue(limit, deductible) : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.overland_water_limit_cad,
    getRawRatedValue: (rated) =>
      (rated?.overland_water as Record<string, unknown> | undefined)?.limit_cad,
    isRequested: (coverages) => coverages.overland_water_enabled,
  },
  {
    label: 'Service Line',
    getRequestedValue: (coverages) =>
      coverages.service_line_enabled
        ? formatCoverageValue(
            coverages.service_line_limit_cad,
            coverages.service_line_deductible_cad,
            true,
          )
        : 'No',
    getRatedValue: (rated) => {
      const serviceLine = rated?.service_line_extension_endorsement as
        | Record<string, unknown>
        | undefined;
      if (!serviceLine) return undefined;
      const limit = serviceLine.limit_cad as number | undefined;
      const deductible = serviceLine.deductible_cad as number | undefined;
      return limit != null ? formatCoverageValue(limit, deductible) : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.service_line_limit_cad,
    getRawRatedValue: (rated) =>
      (
        rated?.service_line_extension_endorsement as
          | Record<string, unknown>
          | undefined
      )?.limit_cad,
    isRequested: (coverages) => coverages.service_line_enabled,
  },
  {
    label: 'By-Law',
    getRequestedValue: (coverages) =>
      coverages.bylaw_enabled
        ? formatCoverageValue(
            coverages.bylaw_limit_cad,
            coverages.bylaw_deductible_cad,
            true,
          )
        : 'No',
    getRatedValue: (rated) => {
      const bylaw = rated?.bylaws_endorsement as
        | Record<string, unknown>
        | undefined;
      if (!bylaw) return undefined;
      const limit = bylaw.limit_cad as number | undefined;
      const deductible = bylaw.deductible_cad as number | undefined;
      return limit != null ? formatCoverageValue(limit, deductible) : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.bylaw_limit_cad,
    getRawRatedValue: (rated) =>
      (rated?.bylaws_endorsement as Record<string, unknown> | undefined)
        ?.limit_cad,
    isRequested: (coverages) => coverages.bylaw_enabled,
  },
  {
    label: 'Fire Department',
    getRequestedValue: (coverages) =>
      coverages.fire_department_enabled
        ? formatCoverageValue(
            coverages.fire_department_limit_cad,
            coverages.fire_department_deductible_cad,
            true,
          )
        : 'No',
    getRatedValue: (rated) => {
      const fireDept = rated?.fire_department_service_charge as
        | Record<string, unknown>
        | undefined;
      if (!fireDept) return undefined;
      const limit = fireDept.limit_cad as number | undefined;
      const deductible = fireDept.deductible_cad as number | undefined;
      return limit != null ? formatCoverageValue(limit, deductible) : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.fire_department_limit_cad,
    getRawRatedValue: (rated) =>
      (
        rated?.fire_department_service_charge as
          | Record<string, unknown>
          | undefined
      )?.limit_cad,
    isRequested: (coverages) => coverages.fire_department_enabled,
  },
  {
    label: 'Identity Theft',
    getRequestedValue: (coverages) =>
      coverages.identity_theft_enabled
        ? formatCoverageValue(
            coverages.identity_theft_limit_cad,
            coverages.identity_theft_deductible_cad,
            true,
          )
        : 'No',
    getRatedValue: (rated) => {
      const identityTheft = rated?.identity_theft as
        | Record<string, unknown>
        | undefined;
      if (!identityTheft) return undefined;
      const limit = identityTheft.limit_cad as number | undefined;
      const deductible = identityTheft.deductible_cad as number | undefined;
      return limit != null ? formatCoverageValue(limit, deductible) : undefined;
    },
    getRawRequestedValue: (coverages) => coverages.identity_theft_limit_cad,
    getRawRatedValue: (rated) =>
      (rated?.identity_theft as Record<string, unknown> | undefined)?.limit_cad,
    isRequested: (coverages) => coverages.identity_theft_enabled,
  },
];

export const COVERAGE_SECTIONS: CoverageSectionConfig[] = [
  {
    key: 'extended',
    label: 'Extended Coverages',
    rows: EXTENDED_COVERAGE_ROWS,
  },
];

export const DISCOUNT_ROWS: DiscountRowConfig[] = [
  {
    label: 'Alarm',
    getRequestedValue: (location) =>
      location.burglar_alarm || location.fire_alarm ? 'Yes' : 'No',
    getRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      const match = discounts.find((d) => /alarm/i.test(d));
      return match || undefined;
    },
    getRawRequestedValue: (location) =>
      !!(location.burglar_alarm || location.fire_alarm),
    getRawRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      return discounts.some((d) => /alarm/i.test(d));
    },
    isRequested: (location) =>
      !!(location.burglar_alarm || location.fire_alarm),
  },
  {
    label: 'Mortgage Free',
    getRequestedValue: (location) =>
      !location.number_of_mortgages ? 'Yes' : 'No',
    getRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      const match = discounts.find((d) => /mortgage.?free/i.test(d));
      return match || undefined;
    },
    getRawRequestedValue: (location) => !location.number_of_mortgages,
    getRawRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      return discounts.some((d) => /mortgage.?free/i.test(d));
    },
    isRequested: (location) => !location.number_of_mortgages,
  },
  {
    label: 'Claims Free',
    getRequestedValue: (location) =>
      location.claims_count === 0 ? 'Yes' : 'No',
    getRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      const match = discounts.find((d) => /claim/i.test(d));
      return match || undefined;
    },
    getRawRequestedValue: (location) => location.claims_count === 0,
    getRawRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      return discounts.some((d) => /claim/i.test(d));
    },
    isRequested: (location) => location.claims_count === 0,
  },
  {
    label: 'Multi-Policy',
    getRequestedValue: (_, insuranceHistory) =>
      insuranceHistory.multi_policy_discount ? 'Yes' : 'No',
    getRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      const match = discounts.find((d) => /multi.?policy/i.test(d));
      return match || undefined;
    },
    getRawRequestedValue: (_, insuranceHistory) =>
      insuranceHistory.multi_policy_discount,
    getRawRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      return discounts.some((d) => /multi.?policy/i.test(d));
    },
    isRequested: (_, insuranceHistory) =>
      insuranceHistory.multi_policy_discount,
  },
  {
    label: 'Multi-Property',
    getRequestedValue: (_, insuranceHistory) =>
      insuranceHistory.multi_property_discount ? 'Yes' : 'No',
    getRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      const match = discounts.find((d) => /multi.?property/i.test(d));
      return match || undefined;
    },
    getRawRequestedValue: (_, insuranceHistory) =>
      insuranceHistory.multi_property_discount,
    getRawRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      return discounts.some((d) => /multi.?property/i.test(d));
    },
    isRequested: (_, insuranceHistory) =>
      insuranceHistory.multi_property_discount,
  },
  {
    label: 'Membership',
    getRequestedValue: (_, insuranceHistory) =>
      insuranceHistory.membership_discount_membership_group ? 'Yes' : 'No',
    getRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      const match = discounts.find((d) => /membership/i.test(d));
      return match || undefined;
    },
    getRawRequestedValue: (_, insuranceHistory) =>
      !!insuranceHistory.membership_discount_membership_group,
    getRawRatedValue: (rated) => {
      const discounts = (rated?.discounts as string[]) || [];
      return discounts.some((d) => /membership/i.test(d));
    },
    isRequested: (_, insuranceHistory) =>
      !!insuranceHistory.membership_discount_membership_group,
  },
];

export const DISCOUNTS_SECTION: DiscountSectionConfig = {
  key: 'discounts',
  label: 'Discounts',
  rows: DISCOUNT_ROWS,
};
